import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Loader2, CheckCircle2, ClipboardList } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SurveyPublicPage() {
  const { token } = useParams();
  const [survey, setSurvey] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [respondent, setRespondent] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${API}/surveys/public/${token}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Encuesta no disponible");
        setSurvey(d);
      })
      .catch((e) => setError(e.message));
  }, [token]);

  const setAnswer = (qid, value) => setAnswers((p) => ({ ...p, [qid]: value }));

  const isVisible = (q) => {
    if (!q.show_if?.question_id) return true;
    const a = answers[q.show_if.question_id];
    if (Array.isArray(a)) return a.includes(q.show_if.value);
    return a === q.show_if.value;
  };

  const visibleQuestions = (survey?.questions || []).filter(isVisible);
  const answeredCount = visibleQuestions.filter((q) => {
    const a = answers[q.id];
    return a !== undefined && a !== "" && !(Array.isArray(a) && a.length === 0);
  }).length;
  const progress = visibleQuestions.length ? Math.round((answeredCount / visibleQuestions.length) * 100) : 0;

  const submit = async () => {
    const missing = visibleQuestions.filter((q) => {
      if (!q.required) return false;
      const a = answers[q.id];
      return a === undefined || a === "" || (Array.isArray(a) && a.length === 0);
    });
    if (missing.length) {
      setError(`Faltan ${missing.length} pregunta(s) obligatoria(s)`);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setSending(true);
    try {
      const visibleAnswers = Object.fromEntries(
        Object.entries(answers).filter(([qid]) => visibleQuestions.some((q) => q.id === qid))
      );
      const r = await fetch(`${API}/surveys/public/${token}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: visibleAnswers, respondent }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Error enviando");
      setDone(true);
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setSending(false);
    }
  };

  if (error && !survey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white p-6">
        <div className="text-center">
          <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-500" />
          <p className="text-lg font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "linear-gradient(135deg, #052e16, #007934)" }}>
        <div className="text-center text-white animate-in fade-in zoom-in duration-500" data-testid="survey-thanks">
          <CheckCircle2 className="w-20 h-20 mx-auto mb-5 text-emerald-300" />
          <h1 className="text-3xl font-black mb-2">¡Gracias!</h1>
          <p className="text-emerald-100/90">Tu respuesta se ha registrado correctamente.</p>
          <p className="text-emerald-200/60 text-xs mt-6">Valoriza · Innovation OS</p>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: "linear-gradient(160deg, #052e16, #0a3d22 50%, #007934)" }} data-testid="survey-public-page">
      <div className="max-w-2xl mx-auto">
        {/* Progress sticky */}
        <div className="sticky top-3 z-10 mb-6">
          <div className="h-2 rounded-full bg-white/10 overflow-hidden backdrop-blur">
            <div className="h-full rounded-full bg-emerald-400 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="text-white mb-8">
          <p className="text-emerald-300 text-xs font-bold uppercase tracking-widest mb-2">Valoriza · Innovation OS</p>
          <h1 className="text-3xl md:text-4xl font-black">{survey.title}</h1>
          {survey.description && <p className="text-emerald-100/80 mt-3 leading-relaxed">{survey.description}</p>}
        </div>

        <div className="space-y-4">
          {visibleQuestions.map((q, i) => (
            <div key={q.id} className="bg-white/95 dark:bg-white rounded-2xl p-5 shadow-xl animate-in fade-in slide-in-from-bottom-2" data-testid={`public-question-${i}`}>
              <p className="font-semibold text-slate-900 mb-3">
                {i + 1}. {q.text} {q.required && <span className="text-red-500">*</span>}
              </p>
              {q.type === "text" && (
                <Textarea
                  className="bg-white text-slate-900"
                  placeholder="Escribe tu respuesta..."
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswer(q.id, e.target.value)}
                  rows={3}
                />
              )}
              {q.type === "single" && (
                <div className="space-y-2">
                  {q.options.map((o) => (
                    <button
                      key={o}
                      onClick={() => setAnswer(q.id, o)}
                      className={`w-full text-left p-3 rounded-xl border-2 text-sm transition-all ${
                        answers[q.id] === o ? "border-emerald-600 bg-emerald-50 font-semibold text-emerald-900" : "border-slate-200 text-slate-700 hover:border-emerald-300"
                      }`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              )}
              {q.type === "multi" && (
                <div className="space-y-2">
                  {q.options.map((o) => {
                    const sel = (answers[q.id] || []).includes(o);
                    return (
                      <button
                        key={o}
                        onClick={() => {
                          const cur = answers[q.id] || [];
                          setAnswer(q.id, sel ? cur.filter((x) => x !== o) : [...cur, o]);
                        }}
                        className={`w-full text-left p-3 rounded-xl border-2 text-sm transition-all ${
                          sel ? "border-emerald-600 bg-emerald-50 font-semibold text-emerald-900" : "border-slate-200 text-slate-700 hover:border-emerald-300"
                        }`}
                      >
                        {sel ? "☑" : "☐"} {o}
                      </button>
                    );
                  })}
                </div>
              )}
              {q.type === "rating" && (
                <div className="flex gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setAnswer(q.id, n)}
                      className={`w-12 h-12 rounded-xl text-lg font-bold transition-all ${
                        answers[q.id] === n ? "bg-emerald-600 text-white scale-110 shadow-lg" : "bg-slate-100 text-slate-600 hover:bg-emerald-100"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              )}
              {q.type === "nps" && (
                <div className="flex gap-1 flex-wrap justify-center">
                  {Array.from({ length: 11 }, (_, n) => (
                    <button
                      key={n}
                      onClick={() => setAnswer(q.id, n)}
                      className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${
                        answers[q.id] === n
                          ? "bg-emerald-600 text-white scale-110 shadow-lg"
                          : n <= 6 ? "bg-red-50 text-red-600 hover:bg-red-100" : n <= 8 ? "bg-amber-50 text-amber-600 hover:bg-amber-100" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="bg-white/95 rounded-2xl p-5 shadow-xl">
            <p className="text-sm text-slate-600 mb-2">Tu nombre o email (opcional)</p>
            <Input className="bg-white text-slate-900" value={respondent} onChange={(e) => setRespondent(e.target.value)} placeholder="Anónimo" data-testid="public-respondent" />
          </div>

          {error && <p className="text-center text-amber-300 text-sm font-semibold">{error}</p>}

          <Button
            onClick={submit}
            disabled={sending}
            className="w-full h-12 text-base font-bold bg-white text-emerald-900 hover:bg-emerald-50"
            data-testid="public-submit-btn"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null} Enviar respuestas
          </Button>
          <p className="text-center text-emerald-200/50 text-xs pb-6">Innovation OS · Valoriza Servicios Medioambientales</p>
        </div>
      </div>
    </div>
  );
}
